/**
 * The one picker for the custom-form fill screen: worker pickers and every
 * dropdown. Tap it, a search box opens at the top of the list, typing filters
 * the list live (anywhere in the label, case-insensitive — "carl" finds
 * Carlos Altamirano and Carl Rolison), tap a row to pick it. Enter picks the
 * top match.
 *
 * A short single-choice list (PLAIN_SELECT_MAX or fewer) stays a native
 * select: there is nothing to search, and the OS menu is the best phone
 * control there is. A multiple-choice list is always the searchable panel,
 * because a native multi-select is unusable on a phone.
 *
 * A value that is not among the options (a rig the template's list does not
 * name, a person outside the loaded list) is still shown as selected, never
 * dropped: the caller passes it in `options`, or it renders under its raw
 * value rather than as an empty picker.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

export const PLAIN_SELECT_MAX = 6;

export interface SearchableOption {
  value: string;
  label: string;
  /** A second, dimmer line (a person's role). Not searched. */
  detail?: string | null;
}

interface Props {
  options: SearchableOption[];
  /** One value, or the set of values when `multiple`. */
  value: string | string[] | null | undefined;
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  /** The field's label — the control's accessible name. */
  label: string;
  placeholder?: string;
  invalid?: boolean;
  id?: string;
}

/** Case-insensitive, matches anywhere in the label. */
export function filterOptions(options: SearchableOption[], query: string): SearchableOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter(o => o.label.toLowerCase().includes(q));
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  multiple = false,
  label,
  placeholder = 'Select...',
  invalid = false,
  id,
}: Props) {
  const selected: string[] = Array.isArray(value) ? value : value ? [value] : [];
  const edge = invalid ? 'border-red-500' : 'border-fieldiq-border';

  if (!multiple && options.length <= PLAIN_SELECT_MAX) {
    const known = options.some(o => o.value === selected[0]);
    return (
      <select
        id={id}
        aria-label={label}
        value={selected[0] ?? ''}
        onChange={e => onChange(e.target.value)}
        className={`w-full bg-fieldiq-card border rounded-lg px-3 py-3 text-white text-sm focus:outline-none focus:border-fieldiq-accent ${edge}`}
      >
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        {selected[0] && !known && <option value={selected[0]}>{selected[0]}</option>}
      </select>
    );
  }

  return (
    <SearchPanel
      options={options}
      selected={selected}
      onChange={onChange}
      multiple={multiple}
      label={label}
      placeholder={placeholder}
      edge={edge}
      id={id}
    />
  );
}

function SearchPanel({
  options, selected, onChange, multiple, label, placeholder, edge, id,
}: {
  options: SearchableOption[];
  selected: string[];
  onChange: (value: string | string[]) => void;
  multiple: boolean;
  label: string;
  placeholder: string;
  edge: string;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const listId = useId();

  const byValue = useMemo(() => new Map(options.map(o => [o.value, o])), [options]);
  const matches = useMemo(() => filterOptions(options, query), [options, query]);
  const labelOf = (v: string) => byValue.get(v)?.label ?? v;

  useEffect(() => {
    if (!open) return;
    search.current?.focus({ preventScroll: true });
    // A picker near the bottom of a phone screen opens below the fold; bring
    // the whole list into view rather than leaving the search box at the edge.
    panel.current?.scrollIntoView?.({ block: 'nearest' });
    const away = (e: MouseEvent | TouchEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
    };
  }, [open]);

  const close = () => { setOpen(false); setQuery(''); };

  const pick = (v: string) => {
    if (!multiple) {
      onChange(v);
      close();
      return;
    }
    onChange(selected.includes(v) ? selected.filter(s => s !== v) : [...selected, v]);
  };

  const summary = selected.length === 0
    ? null
    : multiple ? selected.map(labelOf).join(', ') : labelOf(selected[0]);

  return (
    <div ref={root} className="relative">
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={summary ? `${label}: ${summary}` : label}
        onClick={() => (open ? close() : setOpen(true))}
        className={`flex w-full items-center gap-2 bg-fieldiq-card border rounded-lg px-3 py-3 text-left text-sm focus:outline-none focus:border-fieldiq-accent ${edge}`}
      >
        <span className={`min-w-0 flex-1 truncate ${summary ? 'text-white' : 'text-fieldiq-muted'}`}>
          {summary ?? placeholder}
        </span>
        <ChevronDown size={16} className="shrink-0 text-fieldiq-muted" aria-hidden />
      </button>

      {open && (
        <div ref={panel} className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-lg border border-fieldiq-border bg-fieldiq-card shadow-xl">
          <div className="flex items-center gap-2 border-b border-fieldiq-border px-3">
            <Search size={16} className="shrink-0 text-fieldiq-muted" aria-hidden />
            <input
              ref={search}
              type="text"
              enterKeyHint="done"
              role="combobox"
              aria-controls={listId}
              aria-expanded
              aria-autocomplete="list"
              aria-label={`Search ${label}`}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (matches[0]) pick(matches[0].value);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  close();
                }
              }}
              placeholder="Type to search"
              className="min-w-0 flex-1 bg-transparent py-3 text-base text-white placeholder:text-fieldiq-muted focus:outline-none"
            />
            {query && (
              <button type="button" aria-label="Clear search" onClick={() => setQuery('')} className="p-1 text-fieldiq-muted">
                <X size={16} aria-hidden />
              </button>
            )}
          </div>
          <ul id={listId} role="listbox" aria-label={label} aria-multiselectable={multiple || undefined} className="max-h-72 overflow-y-auto py-1">
            {matches.length === 0 && (
              <li className="px-3 py-3 text-sm text-fieldiq-muted">No matches</li>
            )}
            {matches.map(o => {
              const on = selected.includes(o.value);
              return (
                <li
                  key={o.value}
                  role="option"
                  aria-selected={on}
                  onClick={() => pick(o.value)}
                  className={`flex min-h-[48px] cursor-pointer items-center gap-3 px-3 py-2 hover:bg-fieldiq-dark/50 ${on ? 'bg-fi-amber/10' : ''}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-white">{o.label}</span>
                    {o.detail && <span className="block truncate text-xs text-fieldiq-muted">{o.detail}</span>}
                  </span>
                  {on && <Check size={16} className="shrink-0 text-fieldiq-text" aria-hidden />}
                </li>
              );
            })}
          </ul>
          {multiple && (
            <div className="border-t border-fieldiq-border p-2">
              <button
                type="button"
                onClick={close}
                className="w-full rounded-lg bg-fieldiq-dark py-2.5 text-sm font-semibold text-fieldiq-text"
              >
                Done{selected.length > 0 ? ` (${selected.length})` : ''}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
